import clsx from 'clsx';
import WaveSurfer from 'wavesurfer.js';
import { useCallback, useEffect, useRef, useState } from 'react';
import RecordPlugin from 'wavesurfer.js/dist/plugins/record.esm.js';
import { useToaster } from '@flintmint/web-shared/hooks/use-toaster';

import { Icon } from '@flintmint/web-shared/components/icon';
import { Divider } from '@flintmint/web-shared/components/divider';
import { Collapse } from '@flintmint/web-shared/components/collapse';

import { formatDuration, generateNodeHash } from '@/utils/helper-utils';

import styles from './voice-recorder.module.scss';

export const VoiceRecorder = (props: VoiceRecorderProps) => {
  const { onSave } = props;

  const [open, setOpen] = useState(false);
  const [seconds, setSeconds] = useState(0);
  const [recordingStartErrorType, setRecordingStartErrorType] = useState<RecordingStartErrorType | null>(null);

  const isCancellingRef = useRef(false);
  const timerRef = useRef<number | null>(null);
  const audioUrlRef = useRef<string | null>(null);
  const waveSurferRef = useRef<WaveSurfer | null>(null);
  const waveformRef = useRef<HTMLDivElement | null>(null);
  const recordPluginRef = useRef<RecordPlugin | null>(null);

  const { showToastNotification } = useToaster();

  const stopTimer = useCallback(() => {
    if (!timerRef.current) return;

    clearInterval(timerRef.current);
    timerRef.current = null;
  }, []);

  const startTimer = useCallback(() => {
    stopTimer();
    setSeconds(0);

    timerRef.current = window.setInterval(() => {
      setSeconds((value) => value + 1);
    }, 1000);
  }, [stopTimer]);

  const clearRecording = useCallback(() => {
    if (audioUrlRef.current) {
      URL.revokeObjectURL(audioUrlRef.current);
      audioUrlRef.current = null;
    }

    waveSurferRef.current?.empty();

    setSeconds(0);
  }, []);

  const cleanupRecorder = useCallback(() => {
    stopTimer();

    isCancellingRef.current = true;

    const recordPlugin = recordPluginRef.current;
    const waveSurfer = waveSurferRef.current;

    recordPluginRef.current = null;
    waveSurferRef.current = null;

    setTimeout(() => {
      try {
        if (recordPlugin?.isRecording()) {
          recordPlugin.stopRecording();
        }
      } catch (error) {
        console.warn('[VoiceRecorder] failed to stop recording during cleanup', error);
      }

      setTimeout(() => {
        try {
          recordPlugin?.destroy();
        } catch (error) {
          console.warn('[VoiceRecorder] failed to destroy record plugin', error);
        }

        try {
          waveSurfer?.destroy();
        } catch (error) {
          console.warn('[VoiceRecorder] failed to destroy wavesurfer', error);
        }
      }, 0);
    }, 0);
  }, [stopTimer]);

  const stopRecording = () => {
    if (!recordPluginRef.current?.isRecording()) return;

    recordPluginRef.current.stopRecording();

    stopTimer();
  };

  const cancelRecording = () => {
    isCancellingRef.current = true;
    clearRecording();
    setOpen(false);
  };

  const showMicrophoneAccessToast = useCallback(() => {
    showToastNotification({
      type: 'warning',
      title: `Microphone access wasn't granted. Check your browser settings to allow access.`,
    });
  }, [showToastNotification]);

  const showRequestedDeviceNotFoundToast = useCallback(() => {
    showToastNotification({
      type: 'warning',
      title: 'No microphone input device found. Check your system audio settings and try again',
    });
  }, [showToastNotification]);

  const handleOpen = async () => {
    if (open) return;

    try {
      if (navigator.permissions?.query) {
        try {
          const permission = await navigator.permissions.query({
            name: 'microphone' as PermissionName,
          });

          if (permission.state === 'denied') {
            showMicrophoneAccessToast();
            return;
          }
        } catch (error) {}
      }

      const stream = await navigator.mediaDevices.getUserMedia({
        audio: true,
      });

      stream.getTracks().forEach((track) => track.stop());

      clearRecording();
      setOpen(true);
    } catch (error) {
      if (error instanceof DOMException && error.name === 'NotAllowedError') {
        return showMicrophoneAccessToast();
      }

      if (error instanceof DOMException && error.name === 'NotFoundError') {
        return showRequestedDeviceNotFoundToast();
      }

      console.error(error);

      showToastNotification({
        type: 'warning',
        title: 'Something went wrong while starting the recording.',
      });
    }
  };

  useEffect(() => {
    if (!open || !waveformRef.current) return;

    const waveSurfer = WaveSurfer.create({
      container: waveformRef.current,
      height: 32,
      barWidth: 2,
      barGap: 2,
      barRadius: 999,
      waveColor: '#78A3FF',
      progressColor: '#78A3FF',
      cursorWidth: 0,
      interact: false,
    });

    const recordPlugin = waveSurfer.registerPlugin(
      RecordPlugin.create({
        scrollingWaveform: true,
        scrollingWaveformWindow: 4,
        renderRecordedAudio: false,
        mediaRecorderTimeslice: 100,
      })
    );

    recordPlugin.on('record-end', (blob: Blob) => {
      if (isCancellingRef.current) {
        isCancellingRef.current = false;
        return;
      }

      if (!blob.size) return;

      const isMp4 = blob.type.includes('mp4');
      const recordingName = 'Untitled Voice Recording';
      const fileSuffix = generateNodeHash();

      const file = new File([blob], isMp4 ? `${recordingName}-${fileSuffix}.m4a` : `${recordingName}-${fileSuffix}.webm`, {
        type: isMp4 ? 'audio/m4a' : 'audio/webm',
      });

      onSave?.(file);

      setOpen(false);
    });

    waveSurferRef.current = waveSurfer;
    recordPluginRef.current = recordPlugin;

    const start = async () => {
      try {
        isCancellingRef.current = false;
        clearRecording();

        await recordPlugin.startRecording();

        startTimer();
      } catch (error) {
        console.error('[VoiceRecorder] failed to start recording', error);
        if (error instanceof DOMException && error.name === 'NotFoundError') {
          setRecordingStartErrorType('device_not_found');
          return;
        }
        if (error instanceof DOMException && error.name === 'NotAllowedError') {
          setRecordingStartErrorType('microphone_access_denied');
          return;
        }
        setRecordingStartErrorType('generic');
      }
    };

    start();

    return () => cleanupRecorder();
  }, [open, stopTimer, startTimer, clearRecording, cleanupRecorder]);

  useEffect(() => {
    if (!recordingStartErrorType) return;

    if (recordingStartErrorType === 'microphone_access_denied') {
      showMicrophoneAccessToast();
    } else if (recordingStartErrorType === 'device_not_found') {
      showRequestedDeviceNotFoundToast();
    } else {
      showToastNotification({
        type: 'warning',
        title: 'Something went wrong while starting the recording.',
      });
    }

    setRecordingStartErrorType(null);
  }, [recordingStartErrorType, showMicrophoneAccessToast, showRequestedDeviceNotFoundToast, showToastNotification]);

  useEffect(() => {
    return () => {
      cleanupRecorder();

      if (audioUrlRef.current) {
        URL.revokeObjectURL(audioUrlRef.current);
        audioUrlRef.current = null;
      }
    };
  }, [cleanupRecorder]);

  return (
    <div
      className={clsx(styles.voiceRecorderHolder, {
        [styles.recording]: open,
      })}
      onClick={handleOpen}
    >
      <button type="button" className={styles.titleHolder}>
        <Icon type="mic-01" size={24} />
        <span>{open ? 'Untitled voice Recording' : 'Start a recording'}</span>
      </button>
      <Collapse open={open} className={styles.collapsibleContent} unmountOnCollapse>
        <div className={styles.dividerHolder}>
          <Divider orientation="horizontal" size="100%" />
        </div>
        <div className={styles.voiceCard}>
          <button type="button" aria-label="Cancel recording" className={styles.cancel} onClick={cancelRecording}>
            <Icon type="cancel-01" size={16} />
          </button>
          <div className={styles.wave}>
            <div ref={waveformRef} />
          </div>
          <div className={styles.confirmRecording}>
            <span className={styles.time}>{formatDuration(seconds)}</span>
            <button type="button" aria-label="Save recording" className={styles.confirm} onClick={stopRecording}>
              <Icon type="tick-02" size={16} />
            </button>
          </div>
        </div>
      </Collapse>
    </div>
  );
};

interface VoiceRecorderProps {
  onSave?: (file: File) => void;
}

type RecordingStartErrorType = 'microphone_access_denied' | 'device_not_found' | 'generic';
